import type { ChartAnalyticsMetric } from '@/types/dashboard'
import type { Exchange } from '@/types/generated/common/types'

// ---------------------------------------------------------------------------
// Own-fetch series sources (B2) — a study whose metric is NOT in the shared
// CandleList declares a ChartSeriesSource descriptor. The core
// (SeriesSubscriptionManager + ChartDatafeed routing) is closed to
// modification and open to extension via SeriesSourceRegistry: adding a new
// series later (e.g. options-OI) is one descriptor + one definition.
// ---------------------------------------------------------------------------

/** A normalized point: time in SECONDS, one or more metric values. */
export type SeriesPoint = {
  timeSec: number
  values: Partial<Record<ChartAnalyticsMetric, number>>
}

export type SeriesInputValues = Record<string, string | number | boolean>

export type SeriesFetchContext = {
  symbol: string
  exchange: Exchange
  timeframeSeconds: number
  fromSec: number
  toSec: number
  /** Study input values (e.g. ob-metrics depth tier); part of the cache key. */
  inputs?: SeriesInputValues
}

/** Open id space on purpose: a closed union would force a core-type edit per
 *  added source, defeating the zero-core-edits pluggability contract. */
export type ChartSeriesSourceId = string

export type ChartSeriesSource = {
  id: ChartSeriesSourceId
  /** Metrics this source provides; each maps to one ChartAnalyticsDefinition. */
  metrics: ChartAnalyticsMetric[]
  /** Series gated below this resolution: no fetch, study plots nothing. */
  minTimeframeSeconds?: number
  /** 'bar-close' = manager re-fetches a trailing window on each closed bar.
   *  'stream' = subscribeLive pushes points (documented later-add; no v1
   *  source uses it — the options-volume WS is React-hook-based). */
  refresh: 'bar-close' | 'stream'
  /** Sparse sources (native granularity coarser than the chart timeframe,
   *  e.g. Amberdata PCR/skew) plot the last value at-or-before each bar
   *  instead of exact-bucket-only. */
  carryForward?: boolean
  /** Backfill history for a range. Each source normalizes its own time unit
   *  → seconds and its own response shape → SeriesPoint[]. Aborts via signal. */
  fetchHistory: (ctx: SeriesFetchContext, signal: AbortSignal) => Promise<SeriesPoint[]>
  /** Live join for 'stream' sources; returns an unsubscribe fn. */
  subscribeLive?: (ctx: SeriesFetchContext, onPoints: (points: SeriesPoint[]) => void) => () => void
}

/** Hidden study input bumped to force a TV recompute after async data lands.
 *  Excluded from the inputs hash so bumps never fork cache keys. */
export const REFRESH_NONCE_INPUT_ID = 'refreshNonce'

/** Own-fetch series are coarse; sub-minute timeframes would drive a bar-close
 *  poll (and full study recompute) every second per key. All v1 sources gate
 *  their minTimeframeSeconds at this floor. */
export const SERIES_MIN_TIMEFRAME_SECONDS = 60

export class SeriesSourceRegistry {
  private readonly byId = new Map<ChartSeriesSourceId, ChartSeriesSource>()
  private readonly byMetric = new Map<ChartAnalyticsMetric, ChartSeriesSource>()

  register(source: ChartSeriesSource): void {
    if (this.byId.has(source.id)) {
      throw new Error(`duplicate series source id: ${source.id}`)
    }
    for (const metric of source.metrics) {
      const owner = this.byMetric.get(metric)
      if (owner) {
        throw new Error(`metric ${metric} already owned by source '${owner.id}'`)
      }
    }
    this.byId.set(source.id, source)
    for (const metric of source.metrics) this.byMetric.set(metric, source)
  }

  getById(id: ChartSeriesSourceId): ChartSeriesSource | undefined {
    return this.byId.get(id)
  }

  getByMetric(metric: ChartAnalyticsMetric): ChartSeriesSource | undefined {
    return this.byMetric.get(metric)
  }

  hasMetric(metric: ChartAnalyticsMetric): boolean {
    return this.byMetric.has(metric)
  }
}

/** Stable hash of study inputs for cache keying: sorted `k=v` pairs joined by
 *  '&'; refreshNonce excluded (it changes on every recompute bump). */
export function computeSeriesInputsHash(inputs?: SeriesInputValues): string {
  if (!inputs) return ''
  return Object.keys(inputs)
    .filter((key) => key !== REFRESH_NONCE_INPUT_ID)
    .sort()
    .map((key) => `${key}=${String(inputs[key])}`)
    .join('&')
}

export type SeriesKeyContext = Pick<
  SeriesFetchContext,
  'symbol' | 'exchange' | 'timeframeSeconds' | 'inputs'
>

// Exchange is part of the key: the same symbol (e.g. BTCUSDT) lives on multiple
// venues, and the datafeed survives exchange switches via setExchange — without
// exchange in the key, an exchange switch on an unchanged symbol would keep
// serving the stale venue's cached series.
export function getSeriesKey(sourceId: ChartSeriesSourceId, ctx: SeriesKeyContext): string {
  return `${sourceId}|${ctx.symbol}|${ctx.exchange}|${ctx.timeframeSeconds}|${computeSeriesInputsHash(
    ctx.inputs
  )}`
}
